import React, { useState } from "react";
import { taskAPI } from "../../services/AdminPortal/taskAPI";
import { MessageCircleIcon, ChevronDown } from "lucide-react";
import { toast } from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AssignTask = ({
  isOpen,
  onClose,
  staffData,
  sendNotification,
  adminId,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    deadline: null,
    priority: "",
  });
  const [dateRange, setDateRange] = useState([null, null]);
  const [pickerMode, setPickerMode] = useState("date");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, deadline: date }));
    setIsDatePickerOpen(false);
  };

  const taskTypes = [
    { value: "DOCUMENT_VERIFICATION", label: "Document Verification" },
    { value: "INCOME_VERIFICATION", label: "Income Verification" },
    { value: "LEGAL_CHECK", label: "Legal Check" },
    { value: "CREDIT_CHECK", label: "Credit Check" },
    { value: "LOAN_PROCESSING", label: "Loan Processing" },
    { value: "PROPERTY_VERIFICATION", label: "Property Verification" },
    { value: "BUSINESS_ANALYZER", label: "Business Analyzer" },
    { value: "BANK_STATEMENT_ANALYZER", label: "Bank Statement Analyzer" },
    { value: "CREDIT_ANALYZER", label: "Credit Analyzer" },
    {
      value: "EMPLOYMENT_PAYSLIP_ANALYZER",
      label: "Employment Payslip Analyzer",
    },
    { value: "ITR_ANALYZER", label: "ITR Analyzer" },
    { value: "LOAN_STATEMENT_ANALYZER", label: "Loan Statement Analyzer" },
    { value: "RENTAL_AGREEMENT_ANALYZER", label: "Rental Agreement Analyzer" },
    { value: "BUSINESS_LICENSE_ANALYZER", label: "Business License Analyzer" },
    {
      value: "PROPERTY_DOCUMENT_ANALYZER",
      label: "Property Document Analyzer",
    },
    { value: "EVALUATION_ANALYZER", label: "Evaluation Analyzer" },
    { value: "GENERAL", label: "General Task" },
  ];

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [isPriorityOpen, setIsPriorityOpen] = useState(false);

  const priorityOptions = [
    { value: "", label: "Select Priority" },
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "URGENT", label: "Urgent" },
  ];

  const handlePrioritySelect = (value) => {
    setFormData((prev) => ({ ...prev, priority: value }));
    setIsPriorityOpen(false);
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Basic validation
    if (!formData.title.trim() || !formData.deadline) {
      setError("Task title and due date are required.");
      return;
    }

    setError(null);
    setSending(true);

    try {
      // ✅ Find matching task type
      const selectedTask = taskTypes.find((t) => t.value === formData.title);

      // ✅ Build payload
      const taskPayload = {
        title: selectedTask?.label || formData.title, // human readable
        description: formData.description,
        dueDate: formData.deadline,
        priority: formData.priority || "MEDIUM",
        type: selectedTask?.value || "GENERAL", // backend enum
        assignedTo: staffData._id,
      };

      console.log("Submitting task payload:", taskPayload);

      // ✅ API call
      await taskAPI.createTask(taskPayload);

      // ✅ Notify assignee
      await sendNotification(
        staffData?._id,
        "New task is assigned to you.",
        "Admin",
        adminId
      );

      // ✅ Success handling
      onClose();
      toast.success("Task assigned successfully!");
      setFormData({
        title: "",
        description: "",
        deadline: null,
        priority: "",
      });
    } catch (err) {
      // ✅ Show actual backend error
      console.error(
        "Task assignment error:",
        err.response?.data || err.message
      );
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to assign task";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <style>
        {`
          .react-datepicker-mobile .react-datepicker {
            background-color: #131313 !important;
            border: 1px solid #333333 !important;
            font-size: 12px !important;
          }
          .mobile-calendar {
            background-color: #131313 !important;
            border: 1px solid #333333 !important;
          }
          .react-datepicker__header {
            background-color: #1f1f1f !important;
            border-bottom: 1px solid #333333 !important;
          }
          .react-datepicker__current-month,
          .react-datepicker__day-name {
            color: #FFCA20 !important;
          }
          .react-datepicker__day {
            color: #AAAAAA !important;
          }
          .react-datepicker__day:hover {
            background-color: #333333 !important;
          }
          .react-datepicker__day--selected {
            background-color: #FFCA20 !important;
            color: #131313 !important;
          }
          @media (max-width: 640px) {
            .react-datepicker {
              transform: scale(0.85) !important;
              transform-origin: center !important;
            }
          }
        `}
      </style>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[#131313] p-3 lg:p-6 rounded-lg w-full max-w-[280px] lg:max-w-2xl text-white">
          <div className="flex items-center gap-2 text-[#ffca20] mb-4">
            <MessageCircleIcon />
            <h2 className="text-sm lg:text-lg font-bold">
              Assign task to {staffData.name}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Task Type */}
            <div className="mb-4">
              <label className="block text-xs lg:text-sm text-[#F3F3F3] mb-1">
                Task Type
              </label>
              <select
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333333] rounded p-1 lg:p-2 text-xs lg:text-sm text-[#AAAAAA]"
                disabled={sending}
                required
              >
                <option value="">Select Task Title</option>
                {taskTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-xs lg:text-sm text-[#F3F3F3] mb-1">
                Description (Optional)
              </label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="w-full bg-[#131313] border border-[#333333] rounded p-1 lg:p-2 text-xs lg:text-sm text-[#AAAAAA]"
                disabled={sending}
                placeholder="Enter task description"
              />
            </div>

            {/* Due Date + Priority */}
            <div className="mb-4 flex gap-4">
              <div className="flex-1 relative">
                <label className="block text-xs lg:text-sm text-[#F3F3F3] mb-1">
                  Due Date
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full bg-[#131313] border border-[#333333] rounded p-1 lg:p-2 text-xs lg:text-sm text-[#AAAAAA] text-left flex justify-between items-center"
                    onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                    disabled={sending}
                  >
                    <span>
                      {formData.deadline
                        ? formData.deadline.toLocaleDateString()
                        : "Select due date"}
                    </span>
                    {/* Calendar Icon moved slightly left */}
                    <svg
                      className="ml-2 -mr-1"
                      width="14"
                      height="16"
                      viewBox="0 0 14 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.5V9.5C10 9.78125 10.2188 10 10.5 10H11.5C11.75 10 12 9.78125 12 9.5V8.5C12 8.25 11.75 8 11.5 8H10.5C10.2188 8 10 8.25 10 8.5ZM10 12.5V13.5C10 13.7812 10.2188 14 10.5 14H11.5C11.75 14 12 13.7812 12 13.5V12.5C12 12.25 11.75 12 11.5 12H10.5C10.21875 12 10 12.25 10 12.5ZM6 8.5V9.5C6 9.78125 6.21875 10 6.5 10H7.5C7.75 10 8 9.78125 8 9.5V8.5C8 8.25 7.75 8 7.5 8H6.5C6.21875 8 6 8.25 6 8.5ZM6 12.5V13.5C6 13.7812 6.21875 14 6.5 14H7.5C7.75 14 8 13.7812 8 13.5V12.5C12 12.25 7.75 12 7.5 12H6.5C6.21875 12 6 12.25 6 12.5ZM2 8.5V9.5C2 9.78125 2.21875 10 2.5 10H3.5C3.75 10 4 9.78125 4 9.5V8.5C4 8.25 3.75 8 3.5 8H2.5C2.21875 8 2 8.25 2 8.5ZM2 12.5V13.5C2 13.7812 2.21875 14 2.5 14H3.5C3.75 14 4 13.7812 4 13.5V12.5C4 12.25 3.75 12 3.5 12H2.5C2.21875 12 2 12.25 2 12.5ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V1C3 0.46875 3.4375 0 4 0C4.53125 0 5 0.46875 5 1V2H9V1C9 0.46875 9.4375 0 10 0C10.53125 0 11 0.46875 11 1V2H12.5Z"
                        fill="#FFCA20"
                      />
                    </svg>
                  </button>

                  {isDatePickerOpen && (
                    <div className="absolute z-10 bottom-full mb-2 bg-[#1e1e1e] rounded-md p-3">
                      {/* Picker Mode Switch */}
                      <div className="flex gap-2 mb-2">
                        <button
                          type="button"
                          className={`px-3 py-1 rounded ${
                            pickerMode === "date"
                              ? "bg-yellow-500 text-black"
                              : "bg-[#333] text-white"
                          }`}
                          onClick={() => setPickerMode("date")}
                        >
                          Date
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 rounded ${
                            pickerMode === "month"
                              ? "bg-yellow-500 text-black"
                              : "bg-[#333] text-white"
                          }`}
                          onClick={() => setPickerMode("month")}
                        >
                          Month
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 rounded ${
                            pickerMode === "year"
                              ? "bg-yellow-500 text-black"
                              : "bg-[#333] text-white"
                          }`}
                          onClick={() => setPickerMode("year")}
                        >
                          Year
                        </button>
                      </div>

                      {/* Calendar */}
                      <DatePicker
                        selected={formData.deadline}
                        onChange={handleDateChange}
                        showMonthYearPicker={pickerMode === "month"}
                        showYearPicker={pickerMode === "year"}
                        inline
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Priority */}
              <div className="flex-1 relative">
                <label className="block text-xs lg:text-sm text-[#F3F3F3] mb-1">
                  Priority
                </label>
                <button
                  type="button"
                  onClick={() => setIsPriorityOpen(!isPriorityOpen)}
                  className="w-full bg-[#131313] border-2 border-[#FFCA20] rounded p-1 lg:p-2 text-xs lg:text-sm text-[#AAAAAA] text-left flex justify-between items-center"
                  disabled={sending}
                >
                  <span>
                    {priorityOptions.find(
                      (opt) => opt.value === formData.priority
                    )?.label || "Select Priority"}
                  </span>
                  <ChevronDown size={16} className="text-white" />
                </button>
                {isPriorityOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-[#131313] border border-[#333333] rounded max-h-32 overflow-y-auto">
                    {priorityOptions.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => handlePrioritySelect(option.value)}
                        className="p-2 text-xs lg:text-sm text-[#AAAAAA] hover:bg-[#333333] cursor-pointer"
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Error */}
            {error && <div className="text-red-500 mb-4">{error}</div>}

            {/* Buttons */}
            <div className="flex justify-between gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    title: "",
                    description: "",
                    deadline: null,
                    priority: "",
                  });
                  setError(null);
                  onClose();
                }}
                className="px-4 py-2 bg-transparent border-2 border-white text-white rounded hover:bg-gray-800 transition duration-300"
                disabled={sending}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="px-4 py-2 bg-[#FFCA20] text-black border-2 border-yellow-500 rounded hover:bg-yellow-300 hover:text-gray-800 transition duration-300"
                disabled={sending}
              >
                {sending ? "Assigning..." : "Assign Task ✔"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AssignTask;
